import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import CommonModal from "../components/CommonModal";

const License = () => {
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  // ------------------------------
  // Dynamic Form Field Config
  // ------------------------------
  const formFields = useMemo(() => [
    {
      name: "consignee_id_fk", label: "Consignee", type: "select", required: true,
      options: consigneeOptions,
      col: 6
    },
    { name: "license_no", label: "License No", type: "text", col: 6 },
    {
      name: "license_type", label: "License Type", type: "select",
      options: [
        { value: "", label: "Select --" },
        { value: "AA", label: "Advance Authorization (AA)" },
        { value: "EPCG", label: "EPCG" },
        { value: "DFIA", label: "DFIA" },
        { value: "MEIS", label: "MEIS" },
        { value: "SEIS", label: "SEIS" },
        { value: "IGCRD", label: "IGCRD" }

      ],
      col: 6
    },
    { name: "license_total_value", label: "License Total Value", type: "text", col: 6 },
    { name: "license_used_value", label: "License Used Value", type: "text", col: 6 },
    { name: "license_avail_value", label: "License Avail Value", type: "text", col: 6 },
    { name: "issue_date", label: "Issue Date", type: "date", col: 6 },
    { name: "expiry_date", label: "Expiry Date", type: "date", col: 6 },
    { name: "issuing_authority", label: "Issuing Authority", type: "text", col: 6 },
    { name: "remarks", label: "Remarks", type: "textarea", col: 6 },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: "0", label: "Active" },
        { value: "1", label: "Inactive" },
      ],
      col: 6,
    },
  ], [consigneeOptions]);

  // ------------------------------
  // Initial Form State
  // ------------------------------
  const initialFormState = useMemo(() => {
    return formFields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, { license_id: 0 });
  }, [formFields]);

  const [formData, setFormData] = useState(initialFormState);
  const [licenseList, setLicenseList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    try {
      const response = await post("/compliance/license/list");
      if (response.status === 200) {
        setLicenseList(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  const fetchConsigneeData = useCallback(async () => {
    try {
      const response = await post("/masters/consignees/list");
      if (response.status === 200) {
        setConsigneeOptions([
          { value: "", label: "Select Consignee" },
          ...response.data.map(item => ({
            value: item.consignees_id,
            label: item.consignees_name,
          }))
        ]);
      }
    } catch (err) {
      console.log(err.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchConsigneeData();
  }, [fetchData, fetchConsigneeData]);

  // ------------------------------
  // Edit Handler (FIXED POSITION)
  // ------------------------------
  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });
    setShowModal(true);
  }, [initialFormState]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
      {
        header: "Actions",
        cell: ({ row }) => (
          <i
            className="ri-file-edit-fill text-primary cursor-pointer me-2"
            onClick={() => handleEdit(row)}
            title="Edit"
            style={{ fontSize: "18px" }}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      { header: "Consignee", accessorKey: "consignees_name" },
      { header: "License No", accessorKey: "license_no" },
      { header: "License Type", accessorKey: "license_type" },
      { header: "License Total Value", accessorKey: "license_total_value" },
      { header: "License Used Value", accessorKey: "license_used_value" },
      { header: "License Avail Value", accessorKey: "license_avail_value" },
      { header: "Issue Date", accessorKey: "issue_date" },
      { header: "Expiry Date", accessorKey: "expiry_date" },
      { header: "Issuing Authority", accessorKey: "issuing_authority" },
      { header: "Remarks", accessorKey: "remarks" },
      { header: "Status", accessorKey: "status", label_condition: "status" },
    ],
    [handleEdit]
  );

  // ------------------------------
  // Input Change Handler
  // ------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ------------------------------
  // Close Modal
  // ------------------------------
  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
  };

  // ------------------------------
  // Submit Form
  // ------------------------------
  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    try {
      const response = await post("/compliance/license", formData);
      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        fetchData();
        handleClose();
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">License</div>
          <div className="page-header-sub">AA / EPCG / DFIA licenses</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          Add License
        </button>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">License List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="License List"
            columns={columns}
            data={licenseList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      <CommonModal
        show={showModal}
        title="License List"
        formFields={formFields}
        formData={formData}
        onChange={handleChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
      />
    </>
  );
};

export default License;